import React, {useEffect, useState} from 'react'
import {FormProvider, useForm, useFormContext, Controller} from 'react-hook-form'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {DesktopDatePicker} from '@mui/x-date-pickers';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton, {IconButtonProps} from '@mui/material/IconButton'
import ClientService from "../../../../services/ClientService"
import ClientContext from "../../../../providers/CurrentClientProvider"
import constants from '../../../../constants/constant'
import {associateMarketerList, posSystemList, fiscalYearList} from './constants'
import ClientStatusModal from './ClientStatusModal';
import {
    Typography,
    TextField,
    Button,
    NativeSelect,
    InputLabel,
    FormControlLabel,
    FormControl,
    Checkbox,
    Switch,
    Card,
    CardHeader,
    CardContent
} from "@mui/material"

const cssInJs = {
    header: {
        backgroundColor: "#422e52",
        color: "#fff"
    },
    subheader: {
        color: "#fff"
    },
    headerIcon: {
        backgroundColor: '#f97125',
        color: 'white',
        boxShadow: 'rgba(0,0,0,0.45) 0px 2px 7px 0px',
        position: 'relative',
        top: '41px',
        marginRight: '10px',
        "&:hover": {
            backgroundColor: "#f85e07",
            boxShadow: 'rgba(0,0,0,0.65) 0px 2px 12px 0px',
        },
    },
    warning: {
        backgroundColor: '#FFE264',
        marginTop: '-16px',
        marginBottom: 0,
        marginLeft: '-16px',
        marginRight: '-16px',
        padding: '10px',
        fontSize: '13px'
    },
    textInput: {
        marginBottom: '15px'
    },
    textInputThreeInRowItem: {
        width: '30%'
    },
    select: {
        "&:hover": {
            backgroundColor: "#fff",
        },
        marginBottom: '15px',
    },
    formHeading: {
        fontSize: '15px',
        fontWeight: '500'
    },
}

export const ConnectForm = ({children}) => {
    const methods = useFormContext();

    return children({...methods});
};

const GeneralClientEditForm = () => {
    const [formIsVisible, setFormIsVisible] = useState(true)
    const [clientData, setClientData] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isSharedBilling, setIsSharedBilling] = useState(false)
    const [hasShuttle, setHasShuttle] = useState(false)
    const [hasHeadCount, setHasHeadCount] = useState(false)
    const [isClientStatusModalVisible, setIsClientStatusModalVisible] = useState(false)
    const [formErrors, setFormErrors] = useState([])

    const client_context = ClientContext.useClient()

    const defaultValues = {}
    if (clientData) {
        defaultValues.client_code = clientData.ClientCode
    }

    const methods = useForm({
        mode: 'onChange',
        defaultValues: {
            ...defaultValues,
            changeDate: '',
            FiscalYearDate: '12/31/' + (new Date().getFullYear())
        }
    })

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: {
            errors,
            isDirty
        },
        control
    } = methods

    const handleSharedBillingChange = (event) => {
        setIsSharedBilling(event.target.checked);
    }

    const handleHasShuttleChange = (event) => {
        setHasShuttle(event.target.checked)
    }

    const handleHasHeadCountChange = (event) => {
        setHasHeadCount(event.target.checked)
    }

    const toggleClientStatusModal = () => {
        setIsClientStatusModalVisible(prev => !prev)
    }

    const onSubmit = async (formData) => {
        setFormErrors([])
        try {
            const client = await ClientService.updateExistingClientByCode(clientData.ClientCode,formData)
            client_context.setClient(client)
            setFormIsVisible(false)
        } catch (e) {
            let er = e.response.data.errors
            let arrList = []
            for (const [key, val] of Object.entries(er)) {
                arrList.push(val)
            }
            setFormErrors(arrList)
        }
        // const sample_client_code = await ClientService.save(formData)

    }

    useEffect(() => {
        if (!client_context.client) return

        const fetchClientByCode = async (client_code) => {
            try {
                setIsLoading(true)
                const clientByCode = await ClientService.getClientByCode(client_code, 1)
                setClientData(clientByCode)

                setValue('ClientCode', clientByCode.ClientCode)
                setValue('Name', clientByCode.Name)
                console.log(clientByCode.ClientCode)
                console.log(clientByCode.Name)
                setIsLoading(false)
            } catch (error) {
                console.log(error)
            }
        };

        if (typeof client_context.client === "string") {
            fetchClientByCode(client_context.client)
        } else if (typeof client_context.client === "object") {
            fetchClientByCode(client_context.client.ClientCode)
        }
    }, [client_context.client])

    const selectedAccountStatusValue = watch("selectedAccountStatus");

    const fiscalYearType = watch("fiscalYearType");
    const showFiveWeekMonths = fiscalYearType === '4-5-4' || fiscalYearType === 'Aramark'

    if (!client_context.client) {
        return <>Loading...</>
    }

    if (isLoading) {
        return <>Loading client...</>
    }

    if (!clientData) {
        return <>No client data</>
    }

    return (
        <Card>
            <CardHeader
                title={clientData.Name}
                subheader={clientData.ClientCode}
                subheaderTypographyProps={{sx: cssInJs.subheader}}
                action={
                    <IconButton aria-label="settings" sx={cssInJs.headerIcon} onClick={() => setFormIsVisible(prev => !prev)}>
                        {formIsVisible
                            ? <KeyboardArrowUpIcon/>
                            : <EditIcon/>}
                    </IconButton>
                }
                sx={cssInJs.header}
            />
            <CardContent>
                {isDirty && <Typography sx={cssInJs.warning}>You have unsaved changes, please hit save before
                    continuing.</Typography>}
                {
                    !!formErrors.length &&
                        formErrors.map(((item, i) => {
                            return <p key={'error_' + i}>{item}</p>
                        }))
                }
                {formIsVisible && <FormProvider {...methods} >
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                            General
                        </Typography>
                        <TextField
                            id="client-code"
                            label="Client code"
                            variant="standard"
                            fullWidth
                            sx={cssInJs.textInput}
                            {...register('ClientCode', {
                                required: "Name is required"
                            })}
                            error={Boolean(errors.ClientCode)}
                            helperText={errors.ClientCode?.message}
                        />
                        <TextField
                            id="name"
                            label="Name"
                            variant="standard"
                            fullWidth
                            {...register('Name', {
                                required: "Name is required"
                            })}
                            sx={cssInJs.textInput}
                            error={Boolean(errors.Name)}
                            helperText={errors.Name?.message}
                        />

                        <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                            Primary address
                        </Typography>
                        <TextField
                            id="address-1"
                            label="Address 1"
                            variant="standard"
                            fullWidth
                            {...register('Address1')}
                            sx={cssInJs.textInput}
                        />
                        <TextField
                            id="address-2"
                            label="Address 2"
                            variant="standard"
                            fullWidth
                            {...register('Address2')}
                            sx={cssInJs.textInput}
                        />
                        <TextField
                            id="city"
                            label="City"
                            variant="standard"
                            fullWidth
                            {...register('City')}
                            sx={cssInJs.textInput}
                        />

                        <InputLabel variant="standard" htmlFor="uncontrolled-native">
                            State
                        </InputLabel>
                        <NativeSelect
                            variant="standard"
                            sx={cssInJs.select}
                            fullWidth
                            {...register('State')}
                        >
                            <option value={''}>Select State</option>
                            {constants.STATES.map(state => <option key={state} value={state}>{state}</option>)}
                        </NativeSelect>
                        <TextField
                            id="zip-code"
                            label="Zip Code"
                            variant="standard"
                            fullWidth
                            {...register('zip_code')}
                            sx={cssInJs.textInput}
                        />
                        <TextField
                            id="province"
                            label="Province"
                            variant="standard"
                            fullWidth
                            {...register('Province')}
                            sx={cssInJs.textInput}
                        />
                        <TextField
                            id="country"
                            label="Country"
                            variant="standard"
                            fullWidth
                            {...register('Country')}
                            sx={cssInJs.textInput}
                        />

                        <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                            Contact details
                        </Typography>
                        <div>
                            <TextField
                                id="legal-name"
                                label="Legal Name"
                                variant="standard"
                                fullWidth
                                {...register('LegalName')}
                                sx={cssInJs.textInput}
                            />
                            <TextField
                                id="email"
                                label="Email"
                                variant="standard"
                                fullWidth
                                {...register('Email')}
                                sx={cssInJs.textInput}
                            />
                            <TextField
                                id="phone"
                                label="Phone"
                                variant="standard"
                                fullWidth
                                {...register('Phone')}
                                sx={cssInJs.textInput}
                            />
                            <TextField
                                id="mobile"
                                label="Mobile"
                                variant="standard"
                                fullWidth
                                {...register('Mobile')}
                                sx={cssInJs.textInput}
                            />
                            <TextField
                                id="fax"
                                label="Fax"
                                variant="standard"
                                fullWidth
                                {...register('Fax')}
                                sx={cssInJs.textInput}
                            />
                            <TextField
                                id="skype"
                                label="Skype"
                                variant="standard"
                                fullWidth
                                {...register('Skype')}
                                sx={cssInJs.textInput}
                            />
                            <TextField
                                id="website"
                                label="Website"
                                variant="standard"
                                fullWidth
                                {...register('Website')}
                                sx={cssInJs.textInput}
                            />
                            <TextField
                                id="twitter"
                                label="Twitter"
                                variant="standard"
                                fullWidth
                                {...register('Twitter')}
                                sx={cssInJs.textInput}
                            />
                            <TextField
                                id="facebook"
                                label="Facebook"
                                variant="standard"
                                fullWidth
                                {...register('Facebook')}
                                sx={cssInJs.textInput}
                            />
                        </div>

                        <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                            Technical
                        </Typography>
                        <div>
                            <div>
                                <InputLabel variant="standard" htmlFor="uncontrolled-native">
                                    Associate Marketer
                                </InputLabel>
                                <NativeSelect
                                    variant="standard"
                                    sx={cssInJs.select}
                                    fullWidth
                                    {...register('AssociateMarketerId')}
                                >
                                    <option key="select-associate-marketer" value={''}>Select Associate Marketer</option>
                                    {/* associateMarketerList */}
                                    {associateMarketerList.map(listItem => <option key={listItem.id}
                                                                                   value={listItem.id}>{listItem.name}</option>)}
                                </NativeSelect>
                            </div>
                            <div>
                                <InputLabel variant="standard" htmlFor="uncontrolled-native">
                                    POS System
                                </InputLabel>
                                <NativeSelect
                                    variant="standard"
                                    sx={cssInJs.select}
                                    fullWidth
                                    {...register('PointOfSaleId')}
                                >
                                    <option key="pos-system-title" value={''}>Select point of sale</option>
                                    {posSystemList.map(listItem => <option key={listItem.id}
                                                                           value={listItem.id}>{listItem.name}</option>)}
                                </NativeSelect>
                            </div>

                            <NativeSelect
                                variant="standard"
                                sx={cssInJs.select}
                                fullWidth
                                {...register('FiscalYearType')}
                            >
                                {fiscalYearList.map(listItem => <option key={listItem.value}
                                                                        value={listItem.value}>{listItem.value}</option>)}
                            </NativeSelect>

                            {showFiveWeekMonths && <TextField
                                id="fiveWeekMonths"
                                label="5 Week Months"
                                variant="standard"
                                placeholder="(e.g. 1,4,7,11)"
                                fullWidth
                                {...register('FiveWeekMonths')}
                                sx={cssInJs.textInput}
                            />}

                            <Controller
                                control={control}
                                name='FiscalYearDate'
                                defaultValue={null}
                                render={({field: {onChange, value}}) => (
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DesktopDatePicker
                                            label="Fiscal Year End"
                                            onChange={onChange}
                                            value={value}
                                            {...register('FiscalYearDate', {required: {value: true, message: 'required'}})}
                                            renderInput={(params) => <TextField {...params}
                                                                                fullWidth
                                                                                variant="standard"
                                                                                size="small"
                                                                                sx={{mt: 2}}
                                            />}
                                        />
                                    </LocalizationProvider>
                                )}
                            />

                            <TextField
                                id="imu"
                                label="Default IMU"
                                variant="standard"
                                fullWidth
                                {...register('Imu')}
                                sx={cssInJs.textInput}
                            />
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                                <FormControlLabel
                                    value="top"
                                    control={<Checkbox/>}
                                    label="Has Cash Flow Issue"
                                    {...register('Imu')}
                                    labelPlacement="top"
                                    sx={{alignItems: 'flex-start'}}
                                />
                                <FormControlLabel
                                    value="top"
                                    control={<Checkbox/>}
                                    label="Is EOM Only?"
                                    {...register('HasPlanOnDemand')}
                                    labelPlacement="top"
                                    sx={{alignItems: 'flex-start'}}
                                />
                                <FormControlLabel
                                    value="top"
                                    control={<Checkbox/>}
                                    label="Has Foreign Currency"
                                    labelPlacement="top"
                                    sx={{alignItems: 'flex-start'}}
                                />
                            </div>
                            <TextField
                                id="year-founded"
                                label="Year founded"
                                variant="standard"
                                fullWidth
                                {...register('YearFounded')}
                                sx={cssInJs.textInput}
                            />
                            <TextField
                                id="auth-key"
                                label="RO Auth Key"
                                variant="standard"
                                fullWidth
                                {...register('AuthKey')}
                                sx={cssInJs.textInput}
                            />
                        </div>

                        <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                            Acquisition details
                        </Typography>
                        <div>
                            <InputLabel variant="standard" htmlFor="uncontrolled-native">
                                Referred By
                            </InputLabel>
                            <NativeSelect
                                variant="standard"
                                sx={cssInJs.select}
                                fullWidth
                                {...register('ReferredBy')}
                            >
                                <option value={''}>Select</option>
                            </NativeSelect>
                        </div>

                        <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                            Notes
                        </Typography>
                        <div>
                            <TextField
                                id="notes"
                                label="General notes"
                                variant="standard"
                                fullWidth
                                multiline
                                rows={4}
                                {...register('Notes')}
                                sx={cssInJs.textInput}
                            />
                            <TextField
                                id="competitionNotes"
                                label="Competition notes"
                                variant="standard"
                                fullWidth
                                multiline
                                rows={4}
                                {...register('CompetitionNotes')}
                                sx={cssInJs.textInput}
                            />
                        </div>

                        <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                            Commercial
                        </Typography>
                        <div>
                            <Controller
                                control={control}
                                name='ActiveDate'
                                render={({field: {onChange, value}}) => (
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DesktopDatePicker
                                            label="Active Date"
                                            onChange={onChange}
                                            value={value}
                                            renderInput={(params) => <TextField {...params}
                                                                                fullWidth
                                                                                variant="standard"
                                                                                size="small"
                                                                                sx={{mt: 2}}
                                            />}
                                        />
                                    </LocalizationProvider>
                                )}
                            />
                            <Controller
                                control={control}
                                name='BillingStartDate'
                                render={({field: {onChange, value}}) => (
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DesktopDatePicker
                                            label="Billing Start Date"
                                            onChange={onChange}
                                            value={value}
                                            renderInput={(params) => <TextField {...params}
                                                                                fullWidth
                                                                                variant="standard"
                                                                                size="small"
                                                                                sx={{mt: 2}}
                                            />}
                                        />
                                    </LocalizationProvider>
                                )}
                            />
                        </div>

                        <FormControlLabel
                            control={
                                <Switch checked={isSharedBilling} onChange={handleSharedBillingChange}
                                        name="shared_billing"/>
                            }
                            label="Shared Billing"
                        />

                        <div
                            style={{
                                display: isSharedBilling ? 'none' : 'block'
                            }}
                        >
                            <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                                Rates
                            </Typography>
                            <div style={{display: "flex", justifyContent: "space-between"}}>
                                <TextField
                                    id="otb-rate"
                                    label="OTB"
                                    variant="standard"
                                    {...register('OtbRate')}
                                    sx={cssInJs.textInputThreeInRowItem}
                                />
                                <TextField
                                    id="pnotb-rate"
                                    label="PNOTB"
                                    variant="standard"
                                    {...register('PnotbRate')}
                                    sx={cssInJs.textInputThreeInRowItem}
                                />
                                <TextField
                                    id="notb-rate"
                                    label="NOTB"
                                    variant="standard"
                                    {...register('NotbRate')}
                                    sx={cssInJs.textInputThreeInRowItem}
                                />
                            </div>

                            <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                                Setup Rates
                            </Typography>
                            <div style={{display: "flex", justifyContent: "space-between"}}>
                                <TextField
                                    id="otb-setup-rate"
                                    label="OTB"
                                    variant="standard"
                                    {...register('OtbSetupRate')}
                                    sx={cssInJs.textInputThreeInRowItem}
                                />
                                <TextField
                                    id="pnotb-setup-rate"
                                    label="PNOTB"
                                    variant="standard"
                                    {...register('PnotbSetupRate')}
                                    sx={cssInJs.textInputThreeInRowItem}
                                />
                                <TextField
                                    id="notb-setup-rate"
                                    label="NOTB"
                                    variant="standard"
                                    {...register('NotbSetupRate')}
                                    sx={cssInJs.textInputThreeInRowItem}
                                />
                            </div>

                            <hr style={{border: '1px solid #8b65ac', margin: '25px 0'}}/>

                            <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                                Integrations
                            </Typography>
                            <div>
                                <div style={{display: 'flex'}}>
                                    <FormControlLabel
                                        control={
                                            <Switch checked={hasShuttle} onChange={handleHasShuttleChange}
                                                    name="hasShuttle"/>
                                        }
                                        label="Shuttle Integration"
                                    />
                                    {hasShuttle && <TextField
                                        id="shuttleRate"
                                        label="Shuttle Rate"
                                        variant="standard"
                                        {...register('ShuttleRate')}
                                        sx={{...cssInJs.textInput, width: '20%'}}
                                    />}
                                </div>
                                <div style={{display: 'flex'}}>
                                    <FormControlLabel
                                        control={
                                            <Switch checked={hasHeadCount} onChange={handleHasHeadCountChange}
                                                    name="hasHeadCount"/>
                                        }
                                        label="Headcount Integration"
                                    />
                                    {hasHeadCount && <TextField
                                        id="headCountRate"
                                        label="HeadCount Rate"
                                        variant="standard"
                                        {...register('HeadCountRate')}
                                        sx={{...cssInJs.textInput, width: '20%'}}
                                    />}
                                </div>
                            </div>

                            <hr style={{border: '1px solid #8b65ac', margin: '25px 0'}}/>

                            <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                                Admin
                            </Typography>
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                                <FormControlLabel
                                    control={<Switch {...register('isManualEntry')} name="IsManualEntry"/>}
                                    label="Is Manual Entry?"
                                />
                                <FormControlLabel
                                    control={<Switch {...register('IsTest')} name="IsTest" value={0}/>}
                                    label="Is Demo?"
                                />
                                <FormControlLabel
                                    control={<Switch {...register('IsBeta')} name="IsBeta" value={0}/>}
                                    label="Is Hiatus?"
                                />
                                <FormControlLabel
                                    control={<Switch {...register('HasRovrAccess')} name="HasRovrAccess"/>}
                                    label="Is VIP?"
                                />
                                <FormControlLabel
                                    control={<Switch {...register('IsOnOrion')} name="IsOnOrion"/>}
                                    label="Is being planned in ROV2?"
                                />
                                <FormControlLabel
                                    control={<Switch {...register('HasSharedCustom')} name="HasSharedCustom"/>}
                                    label="Uses shared custom export?"
                                />
                                <FormControlLabel
                                    control={<Switch {...register('HasFaves')} name="HasFaves"/>}
                                    label="Uses Faves Export?"
                                />

                            </div>

                            <hr style={{border: '1px solid #8b65ac', margin: '25px 0'}}/>

                            <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                                POS API Settings
                            </Typography>
                            <div>
                                <TextField
                                    id="apiKey"
                                    label="POS API Key"
                                    variant="standard"
                                    fullWidth
                                    {...register('apiKey')}
                                    sx={cssInJs.textInput}
                                />
                                <TextField
                                    id="apiUrl"
                                    label="POS API URL"
                                    variant="standard"
                                    fullWidth
                                    {...register('apiUrl')}
                                    sx={cssInJs.textInput}
                                />
                                <TextField
                                    id="apiField"
                                    label="POS API Field"
                                    variant="standard"
                                    fullWidth
                                    {...register('apiField')}
                                    sx={cssInJs.textInput}
                                />
                            </div>

                            <hr style={{border: '1px solid #8b65ac', margin: '25px 0'}}/>

                            <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}>
                                Processing Rules
                            </Typography>
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                                <FormControlLabel
                                    control={<Switch {...register('doAutomaticFinalizations')}
                                                     name="doAutomaticFinalizations"/>}
                                    label="Finalize automatically?"
                                />
                                <FormControlLabel
                                    control={<Switch {...register('doNegativeAdjustments')}
                                                     name="doNegativeAdjustments"/>}
                                    label="Do negative adjustments?"
                                />
                                <FormControlLabel
                                    control={<Switch {...register('hasMarkdownInventoryAdjustments')}
                                                     name="hasMarkdownInventoryAdjustments"/>}
                                    label="Has Markdown Inventory Adjustments?"
                                />
                                <FormControlLabel
                                    control={<Switch {...register('doPerpetuateInventory')}
                                                     name="doPerpetuateInventory"/>}
                                    label="Perpetuate Inventory?"
                                />
                                <FormControlLabel
                                    control={<Switch {...register('periodFromTransactionDate')}
                                                     name="periodFromTransactionDate"/>}
                                    label="SkuData Period from Transaction Date?"
                                />
                                <TextField
                                    id="periodOffset"
                                    label="Sku Data Period Offset"
                                    variant="standard"
                                    fullWidth
                                    {...register('periodOffset')}
                                    sx={cssInJs.textInput}
                                />
                            </div>
                            <Typography variant="h4" gutterBottom sx={cssInJs.formHeading}
                                        onClick={toggleClientStatusModal}>
                                Status
                            </Typography>
                            <ClientStatusModal open={isClientStatusModalVisible} onClose={toggleClientStatusModal}
                                               accountState={selectedAccountStatusValue}/>
                        </div>

                        <Button
                            variant="contained"
                            type="submit"
                        >
                            Save Client Profile
                        </Button>
                    </form>
                </FormProvider>}
            </CardContent>
        </Card>
    )
}

export default GeneralClientEditForm
